import { convertPxToRem } from "@/utils";
import theme, { shadows } from "@/theme";

/**
 * Horizontal inset for title + chart stack. Tighter on xs so the Recharts plot has more width.
 */
export const trendGraphCardHorizontalPaddingSx = {
  paddingLeft: { xs: convertPxToRem(8), sm: convertPxToRem(16) },
  paddingRight: { xs: convertPxToRem(8), sm: convertPxToRem(16) },
} as const;

/** Outer paper card wrapping trend line charts (shared layout across Health Score charts) */
export const trendGraphCardPaperSx = {
  width: "100%",
  background: theme.palette.background.paper,
  borderRadius: 2.5,
  pt: 2,
  pb: 2,
  px: 0,
  boxShadow: shadows.light,
  transition: "background-color 0.3s ease",
  outline: "none",
} as const;

/** BMI chart: left margin + Y-axis width vs right margin for zone labels (must stay in sync with overlay Box width) */
export function getBmiTrendChartLayout(isCompact: boolean) {
  return isCompact
    ? { left: 4, right: 76, yAxisWidth: 20 }
    : { left: 8, right: 96, yAxisWidth: 24 };
}

/** Charts with right-hand zone labels (exercise, sleep, mental wellbeing) */
export function getTrendChartLayoutZoneLabels(
  isCompact: boolean,
  fullYAxisWidth: number
) {
  return isCompact
    ? {
        left: 20,
        right: 40,
        yAxisWidth: Math.min(24, fullYAxisWidth),
      }
    : { left: 32, right: 55, yAxisWidth: fullYAxisWidth };
}

/** Vitals charts (margin.left 0 + Y-axis width); tighter right margin on small viewports */
export function getTrendChartLayoutVitals(
  isCompact: boolean,
  fullYAxisWidth: number
) {
  return isCompact
    ? { left: 0, right: 42, yAxisWidth: Math.min(22, fullYAxisWidth) }
    : { left: 0, right: 55, yAxisWidth: fullYAxisWidth };
}

/**
 * Scoped CSS injected on Health Score trend chart cards. Covers:
 * - Focus: removes outlines on focused chart controls (Recharts sets tabindex).
 * - Layout: Recharts’ root SVG defaults to overflow hidden; Y-axis tick labels can
 *   extend past the plot on small widths—`overflow: visible` prevents clipping.
 *
 * @param className - Wrapper class on the chart container (e.g. `blood-pressure-chart`)
 * @returns CSS string for a `<style>` tag
 */
export function getChartFocusStyles(className: string): string {
  return `
    .${className} *:focus,
    .${className} *:focus-visible {
      outline: none !important;
    }
    .${className} svg {
      outline: none !important;
    }
    .${className} .recharts-surface {
      overflow: visible !important;
    }
  `;
}

/**
 * Calculates a dynamic Y-axis domain that expands beyond defaults when data exceeds bounds.
 * Padding ensures dot circles don't clip at the boundary.
 * @param values - Array of numeric data values to consider
 * @param defaultDomain - The default [min, max] domain
 * @param padding - Extra space in data units added beyond min/max values
 * @returns A [min, max] tuple that covers both the default domain and all data
 */
export function calculateDynamicYDomain(
  values: number[],
  defaultDomain: [number, number],
  padding: number = 2
): [number, number] {
  if (values.length === 0) {
    return defaultDomain;
  }
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  return [
    Math.min(defaultDomain[0], Math.floor(minValue - padding)),
    Math.max(defaultDomain[1], Math.ceil(maxValue + padding)),
  ];
}

/**
 * Calculates the position for the chevron indicator
 * @param activeIndex - Index of the active data point
 * @param dataLength - Total number of data points
 * @param leftMargin - Left margin of the chart
 * @param rightMargin - Right margin of the chart
 * @returns CSS left position string
 */
export function getChevronPosition(
  activeIndex: number,
  dataLength: number,
  leftMargin: number = 27,
  rightMargin: number = 55
): string {
  const plotWidthExpr = `(100% - ${leftMargin + rightMargin}px)`;
  if (dataLength <= 1) {
    const fraction = dataLength === 1 ? 0.5 : 0;
    return `calc(${leftMargin}px + ${plotWidthExpr} * ${fraction})`;
  }
  const fraction = activeIndex / (dataLength - 1);
  return `calc(${leftMargin}px + ${plotWidthExpr} * ${fraction})`;
}
